// src/pages/EventDetailPage.js
// Updated: 10/20/2025 08:55 AM (Added Edit Merch Button)

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/common/layout/Layout';
import { useFamily } from '../context/FamilyContext';
import { db } from '../firebase/config';
import { doc, onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
// Import FaPencilAlt
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaTshirt, FaClipboardList, FaGift, FaPoll, FaPencilAlt } from 'react-icons/fa';
import Spinner from '../components/common/Spinner';
import AddItineraryModal from '../components/dashboard/AddItineraryModal';
import ManageMerchModal from '../components/dashboard/ManageMerchModal';
import CreatePollModal from '../components/dashboard/CreatePollModal';
import PhotoGallery from '../components/common/PhotoGallery';
import Poll from '../components/event/Poll';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const { familyData, isHost } = useFamily();
  const [event, setEvent] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [merchandise, setMerchandise] = useState([]);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ type: null, data: null });

  const eventGalleryPath = familyData && eventId ? `families/${familyData.id}/events/${eventId}/gallery` : null;

  useEffect(() => {
    // ... (fetching logic remains the same) ...
    if (!familyData || !eventId) {
        setLoading(false);
        return;
    }
    setLoading(true);
    const eventDocRef = doc(db, `families/${familyData.id}/events/${eventId}`);
    const unsubEvent = onSnapshot(eventDocRef, (doc) => { if (doc.exists()) setEvent({ id: doc.id, ...doc.data() }); else { console.error("Event not found!"); setEvent(null); } }, (error) => { console.error("Error fetching event:", error); setLoading(false); });
    const unsubItinerary = onSnapshot(query(collection(eventDocRef, 'itinerary'), orderBy('date')), (snap) => { setItinerary(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); }, (error) => { console.error("Error fetching itinerary:", error); setLoading(false); });
    const unsubMerch = onSnapshot(query(collection(eventDocRef, 'merchandise'), orderBy('createdAt')), (snap) => setMerchandise(snap.docs.map(d => ({ id: d.id, ...d.data() }))), (error) => console.error("Error fetching merchandise:", error));
    const unsubPolls = onSnapshot(query(collection(eventDocRef, 'polls'), orderBy('createdAt', 'desc')), (snap) => setPolls(snap.docs.map(d => ({ id: d.id, ...d.data() }))), (error) => console.error("Error fetching polls:", error));
    return () => { unsubEvent(); unsubItinerary(); unsubMerch(); unsubPolls(); };
  }, [familyData, eventId]);


  if (loading) { return <Layout><div className="flex justify-center items-center py-20"><Spinner size="lg" /></div></Layout>; }
  if (!event) { return <Layout><div className="text-center py-10"><h1 className="text-2xl font-bold">Event Not Found</h1></div></Layout>; }

  return (
    <Layout>
      {/* Update modal rendering to include editMerch */}
      {modal.type === 'addItinerary' && <AddItineraryModal familyId={familyData.id} eventId={eventId} onClose={() => setModal({type: null})} />}
      {modal.type === 'addMerch' && isHost && <ManageMerchModal familyId={familyData.id} eventId={eventId} onClose={() => setModal({type: null})} />}
      {/* Pass existingMerch data when editing */}
      {modal.type === 'editMerch' && isHost && <ManageMerchModal familyId={familyData.id} eventId={eventId} existingMerch={modal.data} onClose={() => setModal({type: null})} />}
      {modal.type === 'createPoll' && isHost && <CreatePollModal familyId={familyData.id} eventId={eventId} onClose={() => setModal({type: null})} />}

      <div className="space-y-8">
        {/* Event Header Section (unchanged) */}
         <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <h1 className="text-4xl font-bold text-primary">{event.eventName}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-600 mt-4">
                <p className="flex items-center gap-2"><FaCalendarAlt /><strong>Dates:</strong> {format(event.startDate.toDate(), 'MMM d')} - {format(event.endDate.toDate(), 'd, yyyy')}</p>
                <p className="flex items-center gap-2"><FaMapMarkerAlt /><strong>Location:</strong> {event.location}</p>
            </div>
            <p className="mt-4 text-gray-700">{event.description}</p>
            <div className="mt-6">
                <Link to={`/event/${eventId}/register`} className="inline-block bg-cta-orange text-white font-bold py-3 px-8 rounded-lg shadow hover:bg-orange-700 transition-transform transform hover:scale-105">Register Now</Link>
            </div>
        </div>

        {/* Photo Gallery (unchanged) */}
        <PhotoGallery
            storagePath={eventGalleryPath}
            title={`${event.eventName} Photos`}
            emptyMessage={`No photos from ${event.eventName} yet.`}
        />

        {/* Polls Section (unchanged) */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-semibold text-neutral-gray">Family Polls</h2>{isHost && <button onClick={() => setModal({type: 'createPoll'})} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium">Create Poll</button>}</div>
            {polls.length > 0 ? (
                <div className="space-y-6">{polls.map(p => <Poll key={p.id} poll={p} familyId={familyData.id} eventId={eventId} />)}</div>
            ) : (
                <div className="text-center py-8"><FaPoll className="mx-auto text-4xl text-gray-300"/><p className="mt-4 text-gray-500">No polls have been created for this event yet.</p></div>
            )}
        </div>

        {/* Merchandise Section (Updated) */}
        {/* Only show section if host OR if merch exists */}
        {(isHost || merchandise.length > 0) && (
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-neutral-gray">Merchandise</h2>
                {/* Add button only for hosts */}
                {isHost && (
                    <button onClick={() => setModal({type: 'addMerch'})} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium">Add Merchandise</button>
                )}
            </div>
            <div className="space-y-4">
              {merchandise.length > 0 ? ( merchandise.map(item => (
                // Added group class for hover effect on edit button
                <div key={item.id} className="p-4 border rounded-md flex justify-between items-start group">
                    <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><FaTshirt/> {item.itemName} - ${item.basePrice.toFixed(2)}</h3>
                        <p className="text-sm text-gray-600 mt-1">Available Sizes: {item.sizes.map(s => `${s.size}${s.upcharge > 0 ? ` (+$${s.upcharge.toFixed(2)})` : ''}`).join(', ')}</p>
                    </div>
                    {/* Edit button - only for hosts */}
                    {isHost && (
                        <button
                            onClick={() => setModal({ type: 'editMerch', data: item })}
                            className="p-2 text-gray-400 hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit Merchandise"
                        >
                            <FaPencilAlt size={14}/>
                        </button>
                    )}
                </div>
              )) ) : (
                <div className="text-center py-8">
                    <FaGift className="mx-auto text-4xl text-gray-300"/>
                    <p className="mt-4 text-gray-500">No merchandise has been added for this event yet.</p>
                    {/* Message for hosts only */}
                    {isHost && <p className="text-sm text-gray-400 mt-1">Click "Add Merchandise" to create items.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Itinerary Section (unchanged) */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-semibold text-neutral-gray">Reunion Itinerary</h2>{isHost && <button onClick={() => setModal({type: 'addItinerary'})} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium">Add Itinerary Item</button>}</div>
          <div className="space-y-6">
            {itinerary.length > 0 ? ( itinerary.map(item => (<div key={item.id} className="flex flex-col md:flex-row gap-4 border-l-4 border-primary pl-4 py-2"><div className="w-full md:w-1/4"><p className="font-bold text-lg">{format(item.date.toDate(), 'eeee, MMM d')}</p><p className="text-gray-500 flex items-center gap-2"><FaClock size={14}/> {item.time}</p></div><div className="w-full md:w-3/4"><h3 className="font-semibold text-xl text-neutral-gray">{item.title}</h3><p className="font-semibold text-gray-600">{item.locationName}</p><p className="text-sm text-gray-500">{item.address}</p>{item.description && <p className="mt-2 text-gray-700">{item.description}</p>}</div></div>)) ) : (
              <div className="text-center py-8"><FaClipboardList className="mx-auto text-4xl text-gray-300"/><p className="mt-4 text-gray-500">The event itinerary has not been set up yet.</p>{isHost && <p className="text-sm text-gray-400">Click "Add Itinerary Item" to build it out.</p>}</div>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default EventDetailPage;